import React from 'react';
import { User, Mail, Phone, Home, Lock, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const Settings = ({ 
  profile,
  themes,
  selectedTheme,
  profileUpdate,
  setProfileUpdate,
  passwordData,
  setPasswordData,
  handleThemeChange,
  handleProfileUpdate,
  handlePasswordChange,
  handleProfilePictureUpload,
  handleRemoveProfilePicture
}) => {
  return (
    <div className="space-y-6">
      {/* Theme Selection Card */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance Theme</CardTitle>
          <p className="text-sm text-gray-600 mt-1">Choose your preferred color theme</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(themes).map(([key, theme]) => (
              <button
                key={key}
                onClick={() => handleThemeChange(key)}
                className={`relative p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                  selectedTheme === key ? 'border-blue-500 shadow-lg' : 'border-gray-200'
                }`}
              >
                <div className={`w-full h-16 rounded-md bg-gradient-to-r ${theme.header} mb-2`}></div>
                <p className="text-xs font-medium text-gray-700">{theme.name}</p>
                {selectedTheme === key && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Profile Picture Card */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Picture</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-6">
            {/* Current Avatar */}
            <div className="flex-shrink-0">
              {profile.profilePicture ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${profile.profilePicture}`}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover shadow-lg"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold text-3xl shadow-lg">
                  {profile.firstName?.[0]}{profile.lastName?.[0]}
                </div>
              )}
            </div>
            
            {/* Upload Section */}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {profile.firstName} {profile.lastName}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                JPG, PNG or GIF. Max size of 2MB.
              </p>
              <div className="flex space-x-3">
                <label className="cursor-pointer">
                  <Button type="button" variant="outline" className="relative">
                    <User className="w-4 h-4 mr-2" />
                    Upload Photo
                  </Button>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                  />
                </label>
                {profile.profilePicture && (
                  <Button
                    type="button"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={handleRemoveProfilePicture}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <Input
                  type="text"
                  value={profileUpdate.firstName}
                  onChange={(e) => setProfileUpdate({...profileUpdate, firstName: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <Input
                  type="text"
                  value={profileUpdate.lastName}
                  onChange={(e) => setProfileUpdate({...profileUpdate, lastName: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email
                </label>
                <Input
                  type="email"
                  value={profileUpdate.email}
                  onChange={(e) => setProfileUpdate({...profileUpdate, email: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Phone Number
                </label>
                <Input
                  type="tel"
                  value={profileUpdate.phoneNumber}
                  onChange={(e) => setProfileUpdate({...profileUpdate, phoneNumber: e.target.value})}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Home className="w-4 h-4 inline mr-2" />
                  Home Address
                </label>
                <Textarea
                  value={profileUpdate.homeAddress}
                  onChange={(e) => setProfileUpdate({...profileUpdate, homeAddress: e.target.value})}
                  rows={3}
                />
              </div>
            </div>

            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              Update Profile
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="w-4 h-4 inline mr-2" />
                Current Password
              </label>
              <Input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <Input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                required
                minLength={6}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <Input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                required
                minLength={6}
              />
            </div>

            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
